import { mensagemDeErro } from '@common/errors/mensagemDeErro';
import {
  Alert,
  Button,
  Combobox,
  FormField,
  Modal,
  Spinner,
  useToast,
} from '@ntconsult/aluma-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCandidatos, useIncluirParticipantes } from '../hooks';
import type { Candidato } from '../types/Painel';
import type { Ignorado } from './MotivosIgnoradosModal';

const MIN_BUSCA = 2;

type Props = {
  campanhaId: string;
  open: boolean;
  onClose: () => void;
  onResultado: (processados: number, ignorados: Ignorado[]) => void;
};

/** Inclusão manual (spec 14 R8): busca candidatos ativos no Axiis que ainda não estão na campanha. */
export function AdicionarParticipanteModal({ campanhaId, open, onClose, onResultado }: Props) {
  const { t } = useTranslation(['participantes', 'common', 'erros']);
  const { toast } = useToast();
  const [busca, setBusca] = useState('');
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  // Quem já foi escolhido continua com nome mesmo depois de a busca mudar.
  const conhecidos = useRef(new Map<string, Candidato>());
  const candidatos = useCandidatos(campanhaId, busca.trim());
  const incluir = useIncluirParticipantes(campanhaId);

  for (const c of candidatos.data ?? []) conhecidos.current.set(String(c.profissionalId), c);
  useEffect(() => {
    if (!open) {
      setBusca('');
      setEscolhidos([]);
    }
  }, [open]);

  const rotulo = (c: Candidato) =>
    `${c.nomeExibicao}${c.matricula != null ? ` · ${t('participantes:detalhe.matricula', { matricula: c.matricula })}` : ''}`;
  const lista = [
    ...(candidatos.data ?? []),
    ...escolhidos
      .map((id) => conhecidos.current.get(id))
      .filter(
        (c): c is Candidato =>
          !!c && !candidatos.data?.some((d) => d.profissionalId === c.profissionalId),
      ),
  ];

  async function adicionar() {
    try {
      const r = await incluir.mutateAsync(escolhidos.map(Number));
      onResultado(
        r.processados,
        r.ignorados.map((i) => ({
          nome:
            conhecidos.current.get(String(i.profissionalId))?.nomeExibicao ??
            String(i.profissionalId),
          codigo: i.codigo,
        })),
      );
      onClose();
    } catch (e) {
      toast.danger({ title: t(`erros:${mensagemDeErro(e)}`) });
    }
  }

  return (
    <Modal open={open} onClose={onClose} ariaLabel={t('incluir.titulo')}>
      <Modal.Header
        title={t('incluir.titulo')}
        description={t('incluir.somenteAtivos')}
        onClose={onClose}
        dismissLabel={t('common:close')}
      />
      <Modal.Body className="flex flex-col gap-4">
        {candidatos.isError && (
          <Alert variant="danger" title={t(`erros:${mensagemDeErro(candidatos.error)}`)}>
            <Button size="sm" variant="secondary" onClick={() => candidatos.refetch()}>
              {t('incluir.tentarDeNovo')}
            </Button>
          </Alert>
        )}
        {/* Um campo só: a busca fica no painel do combobox (controlada, vai à API) e escolher
            fecha a lista, que de outro modo cobria o rodapé (spec 24 D, pendência 33). */}
        <FormField label={t('incluir.busca')}>
          <Combobox
            multiple
            closeOnSelect
            value={escolhidos}
            onValueChange={setEscolhidos}
            searchValue={busca}
            onSearchChange={setBusca}
            filter={() => true}
            placeholder={t('incluir.busca')}
            aria-label={t('incluir.busca')}
          >
            <Combobox.Trigger />
            <Combobox.Content searchPlaceholder={t('incluir.busca')}>
              <Combobox.List>
                {lista.map((c) => (
                  <Combobox.Option key={c.profissionalId} value={String(c.profissionalId)}>
                    {rotulo(c)}
                  </Combobox.Option>
                ))}
              </Combobox.List>
              {candidatos.isFetching && <Spinner label={t('common:loading')} />}
              {busca.trim().length >= MIN_BUSCA &&
                !candidatos.isFetching &&
                candidatos.data?.length === 0 && (
                  <p className="px-3 py-2 text-sm text-foreground-muted">
                    {t('incluir.semResultado')}
                  </p>
                )}
            </Combobox.Content>
          </Combobox>
        </FormField>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          {t('common:cancel')}
        </Button>
        <Button disabled={escolhidos.length === 0} loading={incluir.isPending} onClick={adicionar}>
          {t('incluir.adicionar', { count: escolhidos.length })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
